/*
  "Keep me signed in on this device" (01-login #6) — the one place that renews a remembered session.

  POST /auth/refresh exchanges the httpOnly refresh cookie (Path=/api/auth, sent only with `withCredentials`) for a
  fresh access token and rotates the cookie. Both AuthContext (a few minutes before the token runs out, and when a
  remembered device opens with an expired token) and lib/api.js (a request answered 401 while the device is still
  remembered) call it; one request at a time per tab, so they never rotate the cookie twice for the same need.

  The page only knows *that* the device is remembered (localStorage `session_remember_until`, an ISO time from the
  server); the secret itself is never readable by scripts.
*/
export const REMEMBER_KEY = 'session_remember_until';

/** Whether this device is remembered right now (the server may still refuse — signed out elsewhere, etc.). */
export function isDeviceRemembered(now = Date.now()) {
  try {
    const until = Date.parse(localStorage.getItem(REMEMBER_KEY) || '');
    return Number.isFinite(until) && until > now;
  } catch {
    return false; // storage blocked: nothing was remembered then either
  }
}

let inFlight = null;

/**
 * Renew the remembered session with `client` (an axios instance whose requests carry the cookie). Resolves with
 * the server's answer ({ token, user, remember_until }); rejects with the axios error (401/403 = refused).
 * Concurrent callers in this tab share one request.
 */
export function refreshRememberedSession(client) {
  if (!inFlight) {
    inFlight = Promise.resolve()
      .then(() => client.post('/auth/refresh', null, { withCredentials: true }))
      .then((response) => {
        const data = response?.data || {};
        if (!data.token) throw Object.assign(new Error('The server did not renew the session'), { response: { status: 401 } });
        return data;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}
